import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { NgClass, NgStyle } from '@angular/common';
import { animate, style, transition, trigger } from '@angular/animations';
import { ModalController } from '@ionic/angular/standalone';
import { MarkdownService as NgxMarkdownService, MarkdownComponent } from 'ngx-markdown';
import { Subject, takeUntil } from 'rxjs';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { EventName } from 'src/app/core/services/analytics/event-names';
import { MarkdownService } from 'src/app/core/services/markdown/markdown.service';
import { safeModalDismiss } from 'src/app/core/services/modal/modal.utils';
import { PayDrawerConfig } from 'src/app/core/services/theme/theme-config';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { ThemePipe } from '../../../core/pipes/theme.pipe';
import { NavButtonComponent } from '../../../ui/buttons/nav-button/nav-button.component';

/**
 * The Solo rules, opened from the Solo sheet's Rules link. The frame must match the tournament
 * rules drawer's, and the body takes the tournament rules' markdown styles, so both rule sheets
 * read alike on every skin. The text is static: `solo-rules` takes no params.
 */
@Component({
  selector: 'app-solo-rules-drawer',
  templateUrl: './solo-rules-drawer.component.html',
  styleUrls: ['./solo-rules-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-in', style({ opacity: 1 })),
      ]),
    ]),
  ],
  imports: [NgStyle, NgClass, NavButtonComponent, MarkdownComponent, ThemePipe],
})
export class SoloRulesDrawerComponent implements OnInit, OnDestroy {
  private modalController = inject(ModalController);
  private analyticsService = inject(AnalyticsService);
  private themeService = inject(ThemeService);
  private markdownService = inject(MarkdownService);
  private ngxMarkdownService = inject(NgxMarkdownService);

  private readonly destroy$ = new Subject<void>();

  readonly rulesContent = signal<string | undefined>(undefined);

  get drawerConfig(): PayDrawerConfig {
    return this.themeService.payDrawerConfig();
  }

  readonly drawerStyles: [string, string][] = [
    ['background', 'drawer'],
    ['border-radius-top', 'drawer'],
  ];

  ngOnInit(): void {
    this.analyticsService.log(EventName.SoloRulesViewed);

    // The renderer is shared by every markdown view, so this must match the tournament rules
    // drawer's override: links open in a new tab, outside the widget.
    this.ngxMarkdownService.renderer.link = ({ href, title, text }) => {
      const titleAttr = title ? ` title="${title}"` : '';
      return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`;
    };

    this.markdownService
      .loadMarkdown('solo-rules')
      .pipe(takeUntil(this.destroy$))
      .subscribe((content) => this.rulesContent.set(content));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async onCloseClick(): Promise<void> {
    await safeModalDismiss(this.modalController);
  }
}
